import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import useDeviceDetection from '../hooks/useDeviceDetection';

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: #111;
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  transition: opacity 0.3s ease, transform 0.3s ease;
  opacity: ${props => props.$isLoaded ? 1 : 0};
  transform: scale(${props => props.$isLoaded ? 1 : 0.95});
  object-fit: ${props => props.$objectFit || 'cover'};

  ${props => props.$hoverEffect && `
    &:hover {
      transform: scale(1.05);
    }
  `}

  /* Mobile-specific optimizations */
  @media (max-width: 768px) {
    ${props => props.$isMobile && `
      image-rendering: -webkit-optimize-contrast;
      image-rendering: optimize-contrast;
    `}
  }
`;

const Placeholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #111;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: ${props => props.$isLoaded ? 0 : 1};
  transition: opacity 0.3s ease;
`;

const LoadingSpinner = styled.div`
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #2c7d46;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const DeviceInfo = styled.div`
  position: absolute;
  top: 5px;
  left: 5px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.7rem;
  display: ${props => props.$showInfo ? 'block' : 'none'};
`;

function ResponsiveImage({
  src,
  alt,
  objectFit = 'cover',
  hoverEffect = false,
  onClick,
  className,
  showDeviceInfo = false,
  baseWidth = 800
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [currentSrc, setCurrentSrc] = useState('');
  const [fallbackAttempts, setFallbackAttempts] = useState(0);
  const maxFallbackAttempts = 3;
  const imgRef = useRef(null);

  const deviceInfo = useDeviceDetection();
  const { isMobile, isTablet, isDesktop, screenWidth, pixelRatio, getOptimalImageSize } = deviceInfo;

  // Generate responsive src based on device capabilities
  useEffect(() => {
    if (!src) return;

    // For mobile devices, try to use smaller/optimized versions
    let responsiveSrc = src;

    if (isMobile || isTablet) {
      // Try to find mobile-optimized versions
      // Check if we can modify the src to load smaller images
      if (src.includes('/images/')) {
        // For collection images, try smaller variants
        if (src.includes('/collections/')) {
          // Try mobile-specific naming patterns
          const mobileSrc = src.replace(/\.jpg$/, '_mobile.jpg');
          const smallSrc = src.replace(/\.jpg$/, '_small.jpg');

          // For now, we'll use the original but add loading="lazy" and smaller fetch priority
          responsiveSrc = src;
        }
      }
    }

    setCurrentSrc(responsiveSrc);
    setFallbackAttempts(0);
    setIsLoaded(false);
  }, [src, isMobile, isTablet, screenWidth]);

  // Set up Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: isMobile ? '25px' : '50px', // Much smaller margin for faster loading
        threshold: 0.1 // Load when just 10% visible
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        observer.disconnect();
      }
    };
  }, [isMobile]);

  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  const handleImageError = () => {
    console.error(`[ResponsiveImage] Failed to load image: ${currentSrc} (Attempt ${fallbackAttempts + 1}/${maxFallbackAttempts})`);

    if (fallbackAttempts < maxFallbackAttempts) {
      const nextAttempt = fallbackAttempts + 1;
      setFallbackAttempts(nextAttempt);

      // First attempt: try direct path
      if (nextAttempt === 1) {
        try {
          const parts = currentSrc.split('/');
          const filename = parts[parts.length - 1];
          const directPath = `/images/${filename}`;
          console.log(`[ResponsiveImage] Trying direct path: ${directPath}`);
          setCurrentSrc(directPath);
          return;
        } catch (error) {
          console.error('[ResponsiveImage] Error creating direct path:', error);
        }
      }
      // Second attempt: try alternative naming
      else if (nextAttempt === 2) {
        try {
          const parts = currentSrc.split('/');
          const filename = parts[parts.length - 1];

          if (/\d+\.jpg$/.test(filename)) {
            const altPath = filename.replace(/(\d+)\.jpg$/, '$1_large.jpg');
            const newPath = `/images/collections/${parts[parts.length - 2]}/${altPath}`;
            console.log(`[ResponsiveImage] Trying alternative naming: ${newPath}`);
            setCurrentSrc(newPath);
            return;
          }

          const rootPath = `/${filename}`;
          console.log(`[ResponsiveImage] Trying root path: ${rootPath}`);
          setCurrentSrc(rootPath);
          return;
        } catch (error) {
          console.error('[ResponsiveImage] Error creating alternative path:', error);
        }
      }
      // Final attempt: placeholder
      else if (nextAttempt >= maxFallbackAttempts - 1) {
        console.log('[ResponsiveImage] Using placeholder as final fallback');
        setCurrentSrc('/images/placeholder.jpg');
        return;
      }
    }

    console.log('All fallback attempts exhausted');
    setIsLoaded(true);
  };

  const optimalSize = getOptimalImageSize(baseWidth);

  return (
    <ImageContainer className={className} ref={imgRef} onClick={onClick}>
      {showDeviceInfo && (
        <DeviceInfo $showInfo={showDeviceInfo}>
          {isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop'} • {screenWidth}px • {pixelRatio}x
        </DeviceInfo>
      )}

      {isInView && (
        <StyledImage
          src={currentSrc}
          alt={alt}
          onLoad={handleImageLoad}
          onError={handleImageError}
          $objectFit={objectFit}
          loading="lazy"
          decoding="async"
          fetchPriority="auto"
          sizes={isMobile ? "100vw" : isTablet ? "50vw" : "33vw"}
          $isLoaded={isLoaded}
          $hoverEffect={hoverEffect && !isMobile} // Disable hover effects on mobile
          $isMobile={isMobile}
        />
      )}

      <Placeholder $isLoaded={isLoaded}>
        <LoadingSpinner />
      </Placeholder>
    </ImageContainer>
  );
}

export default ResponsiveImage;